import { useState, useEffect } from "react";
import { 
  getCustomizeProducts_pod, 
  getFilteredProducts
} from "../../../../api/catalog_product.api";
import Pagination from "../../../../components/DataTable/Pagination";
import { SkeletonBodyText } from "@shopify/polaris";
import { toast } from "react-toastify";

import productImg from "./assets/product.png";
import cat1 from "./assets/cat1.png";
import cat17 from "./assets/cat17.png";

import Categories from "./Categories";
import CategoriesList from "./CategoriesList";
import ProductsList from "./ProductsList";

// Product sets that can be filtered
const PRODUCT_SETS = {
  RECOMMENDED: 'recommended',
  BEST_SELLERS: 'best-sellers',
  NEW_ARRIVALS: 'new-arrivals',
  SEASONAL: 'seasonal'
};

const productCategories = [
  {
    category: "Drinkware",
    img: cat1,
    sub: [
      "Tumblers",
      "Travel Mugs",
      "Water Bottles",
      "Coffee Mugs",
      "Can Coolers",
      "Wine Cups",
      "Pint Glasses",
      "Pilsner Glasses",
      "Accessories",
    ],
  },
  // {
  //   category: "Hats",
  //   img: cat17,
  //   sub: [
  //     "Leather Patch Hats",
  //     "Snapback Hats",
  //     "Trucker Hats",
  //     "Embroidered Hats",
  //     "Beanies",
  //   ],
  // },
];

// Define interface for auth error
interface AuthError {
  isAuthError?: boolean;
  originalError?: any;
}

interface Product {
  CustomizedProductID: number;
  title: string;
  productImage: string;
  label: string;
  reviewsCount: number;
  colors: string[];
  sizes: string | null;
  price: number | null;
  priceWithPlus: number | null;
  deliveryDays: string | null;
  CustomizedProductVariants: any[];
  shipping_method_details: any;
  subCategory: string | null;
}

const Collections = () => {
  const [pageLoading, setPageLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [queryValue, setQueryValue] = useState({ search: "" });
  const [productCatalog, setProductCatalog] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [activeCategory, setActiveCategory] = useState("Drinkware");
  const [activeSubCategory, setActiveSubCategory] = useState("");
  const [hasMounted, setHasMounted] = useState(false);
  const [productSet, setProductSet] = useState(PRODUCT_SETS.RECOMMENDED);
  const [sortBy, setSortBy] = useState("title");

  const sortProducts = (products: Product[], sortType: string): Product[] => {
    return [...products].sort((a, b) => {
      switch (sortType) {
        case "title":
          return a.title.localeCompare(b.title);
        case "price":
          const priceA = a.CustomizedProductVariants?.[0]?.price || 0;
          const priceB = b.CustomizedProductVariants?.[0]?.price || 0;
          return priceA - priceB;
        default:
          return 0;
      }
    });
  };

  const callFunction = async () => {
    setPageLoading(true);
    
    try {
      // Use the filtered products API with correct parameters
      const res = await getFilteredProducts(currentPage, 'kodiak', { 
        category: activeCategory,
        limit: 12,
      });
      
      const { products, pageCount } = res;
      res?.pageCount && setPageCount(pageCount);
      
      if (!products || products.length === 0) {
        setProductCatalog([]);
        setFilteredProducts([]);
        setPageLoading(false);
        return;
      }
      
      const newArray = products.map((item: any) => {
        if (!item) return null;
        
        const variant = item.CustomizedProductVariants && item.CustomizedProductVariants[0];
        const shippingData = item.shipping_data && item.shipping_data[0];
        
        return {
            CustomizedProductID: item.CustomizedProductID,
            title: item.title,
            productImage: item.productImage || productImg,
            label: "Kodiak Suggests",
            reviewsCount: 1185,
            colors: item.CustomizedProductVariants?.map((i: any) => i.option1) || [],
            sizes: item.CustomizedProductoptions && item.CustomizedProductoptions[1]
                ? item.CustomizedProductoptions[1].value
                : null,
            price: variant ? variant.price : null,
            priceWithPlus: variant ? variant.price * 0.8 : null,
            deliveryDays: shippingData && shippingData.shipDayMax !== shippingData.shipDayMin
                ? `${shippingData.shipDayMin} - ${shippingData.shipDayMax}`
                : shippingData ? shippingData.shipDayMax : null,
            CustomizedProductVariants: item.CustomizedProductVariants || [],
            shipping_method_details: item.shipping_method_details,
            subCategory: item.subCategory || null
        };
      }).filter(Boolean); // Remove any null items
      
      setProductCatalog(newArray);
      setFilteredProducts(newArray);
    } catch (err: any) {
      console.error("Error fetching products:", err);
      // Check if it's an auth error from our interceptor
      if (err && (err as AuthError).isAuthError) {
        toast.error("Please refresh the page and login again to continue.");
      } else {
        toast.error("Failed to load products. Please try again.");
      }
      setProductCatalog([]);
      setFilteredProducts([]);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    // Only make API call if we're changing main categories
    const isMainCategory = productCategories.some(cat => cat.category === activeCategory);
    if (isMainCategory) {
      callFunction();
    }
  }, [activeCategory]);

  useEffect(() => {
    let filtered = [...productCatalog];

    // Apply search filter
    if (queryValue.search.trim()) {
      const searchTerm = queryValue.search.toLowerCase();
      filtered = filtered.filter((product: Product) => 
        product.title.toLowerCase().includes(searchTerm)
      );
    }

    // Apply subcategory filter
    if (activeSubCategory) {
      filtered = filtered.filter((product: Product) => 
        product.subCategory === activeSubCategory
      );
    }

    // Apply sorting
    filtered = sortProducts(filtered, sortBy);

    setFilteredProducts(filtered);
  }, [queryValue.search, productCatalog, activeSubCategory, sortBy]);

  const handlePageChange = (newPage: any) => {
    setCurrentPage(newPage);
  };

  const handleProductSetChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setProductSet(event.target.value);
    setCurrentPage(1); // Reset to first page when changing product set
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(event.target.value);
  };

  const loadingPageMarkup = <SkeletonBodyText lines={9} />;

  useEffect(() => {
    if (hasMounted) {
      setQueryValue({ search: activeCategory });
    } else {
      setHasMounted(true);
    }
  }, [activeCategory]);

  return (
    <>
      <div className="flex max-md:flex-col items-center justify-between gap-5 py-4 px-2 mb-4 border-b border-t border-gray-200">
        <div className="w-1/2 max-md:w-full max-sm:flex max-sm:items-center max-sm:justify-start max-sm:gap-2">
          <input
            className="inline p-2 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-[5px] focus:outline-none focus:bg-white min-w-[260px] max-sm:min-w-[240px] mr-4"
            type="text"
            placeholder="Search within this category"
            onChange={(e) => setQueryValue({ search: e.target.value })}
          />{" "}
          <span className="text-[#5c5c5c]">
            {filteredProducts.length} results
          </span>
        </div>
        <div className="max-md:w-full max-sm:flex max-sm:items-center max-sm:justify-start max-sm:gap-4">
          <strong>Sort by</strong>{" "}
          <select
            className="inline px-2 py-2.5 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-[5px] focus:outline-none focus:bg-white min-w-[260px]"
            name="sortBy"
            id="sortBy"
            value={sortBy}
            onChange={handleSortChange}
          >
            <option value="title">Name (A-Z)</option>
            <option value="price">Price (Low to High)</option>
            <option value="featured">Featured</option>
          </select>
        </div>
      </div>
      <div className="flex max-md:flex-col max-md:items-center gap-5">
        <div className="w-1/4 max-md:w-full">
          <Categories
            productCategories={productCategories}
            activeCategory={activeCategory}
            activeSubCategory={activeSubCategory}
            setActiveCategory={setActiveCategory}
            setActiveSubCategory={setActiveSubCategory}
          />
        </div>
        <div className="w-3/4 max-md:w-full max-sm:max-w-full">
          {!activeCategory ? (
            <CategoriesList
              productCategories={productCategories}
              setActiveCategory={setActiveCategory}
            />
          ) : pageLoading ? (
            loadingPageMarkup
          ) : filteredProducts.length > 0 ? (
            <ProductsList productCatalog={filteredProducts} />
          ) : (
            <div className="text-center py-8 text-gray-500">No products found</div>
          )}
          {filteredProducts.length > 0 && (
            <Pagination
              pageCount={pageCount}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Collections;
